/**
 * Files on a ticket: what is already there, and what is about to be.
 *
 * Two components, deliberately separate, because they are two different
 * things wearing similar clothes:
 *
 *   `AttachmentList`  files that EXIST. Server truth, downloadable.
 *   `StagedFiles`     files chosen but not yet sent. Local, removable, and
 *                     not yet anybody else's business.
 *
 * Conflating them is how an interface ends up offering "remove" on something
 * already emailed to a requester, or a download link for bytes that have not
 * been uploaded.
 *
 * Both are pure — no queries, no mutations, no toasts. They take data and
 * callbacks. That is what lets them be rendered and looked at without
 * standing up the app, which matters here because the local stack signs in
 * through Entra and a headless screenshot never reaches a ticket page.
 */
import { useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import * as RD from '@radix-ui/react-dialog';
import { api } from '../lib/api.js';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  Paperclip,
  X,
} from 'lucide-react';

export interface Attachment {
  id: string;
  conversationId: string | null;
  filename: string;
  contentType: string;
  sizeBytes: number;
  scan: 'scan_pending' | 'scan_clean' | 'scan_infected' | 'scan_skipped';
  createdAt?: string;
}

/**
 * Bytes as a person reads them.
 *
 * Deliberately coarse: nobody needs three significant figures on an email
 * attachment, and "1.4 MB" scans faster than "1,432,117 bytes" in a row of
 * files somebody is skimming.
 */
export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * An icon that says what KIND of thing this is at a glance.
 *
 * From the declared type, which is a claim rather than a fact — but an icon
 * is the one place a wrong claim costs nothing. It is never used to decide
 * how the file is served; that is always a download. See the route.
 */
function iconFor(contentType: string, className: string): ReactNode {
  const t = (contentType || '').toLowerCase();
  if (t.startsWith('image/')) return <FileImage aria-hidden className={className} />;
  if (t.includes('spreadsheet') || t.includes('excel') || t.includes('csv')) {
    return <FileSpreadsheet aria-hidden className={className} />;
  }
  if (t.includes('pdf') || t.startsWith('text/') || t.includes('word') || t.includes('document')) {
    return <FileText aria-hidden className={className} />;
  }
  return <FileIcon aria-hidden className={className} />;
}

/**
 * Getting the bytes to the user, with the credential attached.
 *
 * A BUTTON, NOT A LINK, and the reason matters more than the markup.
 *
 * These were `<a href download>` and `<img src>` pointing straight at the
 * API, on the reasoning that the browser already knows how to fetch a URL
 * with the session. It does - with a COOKIE session. This application
 * authenticates with a bearer token in a header, and a browser navigation
 * carries no header, so every click answered 401 and the browser reported
 * "file not available on the site" for a file sitting intact in storage.
 * Every thumbnail 401ed the same way and fell back silently to a chip.
 *
 * A link would also promise what it cannot keep: middle-click,
 * open-in-new-tab and "save link as" all navigate, and all of them 401.
 */
function useDownload(tenant: string, a: Attachment) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const run = useCallback(() => {
    if (busy) return;
    setBusy(true);
    setFailed(null);
    void api
      .download(`/api/v1/tenants/${tenant}/attachments/${a.id}`, {
        accept: '*/*',
        fallbackName: a.filename,
      })
      .catch((err: unknown) => {
        setFailed(err instanceof Error ? err.message : 'that file could not be downloaded');
      })
      .finally(() => setBusy(false));
  }, [busy, tenant, a.id, a.filename]);

  return { run, busy, failed };
}

/**
 * The preview bytes, fetched WITH the token, rendered from an object URL.
 *
 * The URL is revoked when the component goes away; an un-revoked one pins
 * the whole image in memory for the life of the tab, and a thread with a
 * dozen screenshots gets opened and closed all day.
 */
function usePreview(path: string): { src: string | null; failed: boolean } {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setSrc(null);
    setFailed(false);

    void (async () => {
      try {
        const blob = await api.blob(path);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      } catch {
        // A 415 is the server declining to preview something that is not
        // genuinely an image. Falling back to a chip is the whole design.
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);

  return { src, failed };
}

/** Why a download failed, under the thing that failed. */
function Failure({ message }: { message: string }) {
  return (
    <span className="mt-0.5 block max-w-[18rem] text-2xs leading-snug text-danger">{message}</span>
  );
}

/** One file as a download row. The fallback for anything not displayable. */
function Chip({ attachment: a, tenant }: { attachment: Attachment; tenant: string }) {
  const { run, busy, failed } = useDownload(tenant, a);

  return (
    <span className="inline-block">
      <button
        type="button"
        onClick={run}
        aria-busy={busy}
        className="group inline-flex max-w-[18rem] items-center gap-2 rounded-md border border-line bg-surface px-2.5 py-1.5 text-left text-xs text-ink-secondary transition-colors hover:border-brand hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        title={`${a.filename} · ${humanSize(a.sizeBytes)}`}
      >
        {busy ? (
          <Loader2 aria-hidden className="h-3.5 w-3.5 shrink-0 animate-spin text-brand" />
        ) : (
          iconFor(a.contentType, 'h-3.5 w-3.5 shrink-0 text-ink-tertiary group-hover:text-brand')
        )}
        <span className="truncate">{a.filename}</span>
        <span className="shrink-0 text-2xs text-ink-tertiary">{humanSize(a.sizeBytes)}</span>
        <Download
          aria-hidden
          className="h-3 w-3 shrink-0 text-ink-tertiary opacity-0 transition-opacity group-hover:opacity-100"
        />
      </button>
      {failed && <Failure message={failed} />}
    </span>
  );
}
/** Worth trying to render? The server has the final say. */
export function looksDisplayable(contentType: string): boolean {
  // SVG is an image and is NOT here: it is XML, it can carry script, and the
  // preview endpoint refuses it. Asking for one would only cost a 415.
  return /^image\/(png|jpeg|jpg|gif|webp)$/i.test((contentType || '').trim());
}

/**
 * Displayable files, shown rather than listed.
 *
 * WHETHER TO TRY is decided from the declared type, which is only a claim —
 * but the SERVER decides what it will actually serve, from the bytes, and
 * answers 415 for anything that is not genuinely one of four raster formats.
 * So a mislabelled file costs one failed request and falls back to a chip,
 * and a malicious one is never rendered at all. The claim is allowed to be
 * wrong here precisely because nothing downstream trusts it.
 *
 * The chip stays underneath rather than the image being a link: the bytes
 * on screen came through a fetch, and only the chip can actually save the
 * file. A thumbnail somebody cannot download is a picture of a file rather
 * than a file.
 */
function Thumbnail({
  attachment: a,
  tenant,
  onOpen,
}: {
  attachment: Attachment;
  tenant: string;
  /** Opens the lightbox on this image. */
  onOpen: () => void;
}) {
  const preview = usePreview(`/api/v1/tenants/${tenant}/attachments/${a.id}/preview`);
  const { run, busy, failed } = useDownload(tenant, a);

  // Not previewable, or the bytes never came: the chip is the honest
  // fallback and still downloads.
  if (preview.failed) return <Chip attachment={a} tenant={tenant} />;

  return (
    <span className="inline-block">
      {/* The tile OPENS the picture; the caption's icon saves it. A
          screenshot is usually there to be looked at, not filed. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${a.filename}`}
        className="group block overflow-hidden rounded-md border border-line bg-surface text-left transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        title={`${a.filename} · ${humanSize(a.sizeBytes)}`}
      >
        {preview.src === null ? (
          // Held at roughly the size a thumbnail lands at, so the thread
          // does not jump when the bytes arrive.
          <span className="flex h-28 w-40 items-center justify-center bg-surface-sunken">
            <Loader2 aria-hidden className="h-4 w-4 animate-spin text-ink-tertiary" />
          </span>
        ) : (
          <img
            src={preview.src}
            alt={a.filename}
            className="block max-h-44 w-auto max-w-[16rem] object-contain"
          />
        )}
      </button>
      <span className="flex items-center gap-2 px-0.5 pt-0.5 text-2xs text-ink-tertiary">
        <span className="max-w-[12rem] truncate">{a.filename}</span>
        <span className="shrink-0">{humanSize(a.sizeBytes)}</span>
        <button
          type="button"
          onClick={run}
          aria-busy={busy}
          aria-label={`Download ${a.filename}`}
          title="Download"
          className="ml-auto rounded p-0.5 hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          {busy ? (
            <Loader2 aria-hidden className="h-3 w-3 animate-spin text-brand" />
          ) : (
            <Download aria-hidden className="h-3 w-3" />
          )}
        </button>
      </span>
      {failed && <Failure message={failed} />}
    </span>
  );
}

/**
 * The picture at full size, over the page.
 *
 * The same authenticated preview the thumbnail uses, never an <img> pointed
 * at the API, which 401s (see `useDownload`). Download is right there, and
 * with several pictures on one message the arrows (and the arrow keys) step
 * between them.
 */
function Lightbox({
  images,
  index,
  tenant,
  onIndex,
  onClose,
}: {
  images: Attachment[];
  index: number;
  tenant: string;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const a = images[index]!;
  const preview = usePreview(`/api/v1/tenants/${tenant}/attachments/${a.id}/preview`);
  const { run, busy, failed } = useDownload(tenant, a);
  const many = images.length > 1;
  const step = (d: number) => onIndex((index + d + images.length) % images.length);

  return (
    <RD.Root open onOpenChange={(open) => !open && onClose()}>
      <RD.Portal>
        <RD.Overlay className="anim-overlay fixed inset-0 z-50 bg-black/80" />
        <RD.Content
          className="anim-content fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 p-4 focus:outline-none"
          onKeyDown={(e) => {
            if (!many) return;
            if (e.key === 'ArrowRight') step(1);
            if (e.key === 'ArrowLeft') step(-1);
          }}
        >
          <RD.Title className="sr-only">{a.filename}</RD.Title>
          <RD.Description className="sr-only">Image attachment, {humanSize(a.sizeBytes)}</RD.Description>
          <div className="flex max-h-[80vh] max-w-[92vw] items-center justify-center">
            {preview.src === null ? (
              preview.failed ? (
                <span className="text-sm text-white/80">This image could not be shown. Download it instead.</span>
              ) : (
                <Loader2 aria-hidden className="h-6 w-6 animate-spin text-white/80" />
              )
            ) : (
              <img
                src={preview.src}
                alt={a.filename}
                className="max-h-[80vh] max-w-[92vw] rounded object-contain shadow-2xl"
              />
            )}
          </div>
          <div className="flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-xs text-white">
            {many && (
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Previous image"
                className="rounded-full p-1 hover:bg-white/15"
              >
                <ChevronLeft aria-hidden className="h-4 w-4" />
              </button>
            )}
            <span className="max-w-[40vw] truncate">{a.filename}</span>
            <span className="text-white/60">
              {humanSize(a.sizeBytes)}
              {many ? ` · ${index + 1} of ${images.length}` : ''}
            </span>
            {many && (
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Next image"
                className="rounded-full p-1 hover:bg-white/15"
              >
                <ChevronRight aria-hidden className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={run}
              aria-busy={busy}
              className="ml-1 inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 hover:bg-white/25"
            >
              {busy ? (
                <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download aria-hidden className="h-3.5 w-3.5" />
              )}
              Download
            </button>
            <RD.Close className="rounded-full p-1 hover:bg-white/15" aria-label="Close">
              <X aria-hidden className="h-4 w-4" />
            </RD.Close>
          </div>
          {failed && <span className="text-xs text-red-300">{failed}</span>}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

export function AttachmentList({
  attachments,
  tenant,
  compact = false,
}: {
  attachments: Attachment[];
  tenant: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState<number | null>(null);
  if (attachments.length === 0) return null;

  /**
   * Pictures first, then everything else.
   *
   * A screenshot is usually the point of the message — "here is the error" —
   * and a row of chips with one image buried among them buries the thing the
   * reader came for. Grouping also stops the layout lurching as thumbnails
   * load at different heights.
   */
  const shown = [...attachments].sort(
    (a, b) => Number(looksDisplayable(b.contentType)) - Number(looksDisplayable(a.contentType)),
  );

  const images = shown.filter((a) => looksDisplayable(a.contentType));

  return (
    <>
      <ul className={`flex flex-wrap items-start gap-1.5 ${compact ? '' : 'mt-2'}`}>
        {shown.map((a) => (
          <li key={a.id}>
            {looksDisplayable(a.contentType) ? (
              <Thumbnail attachment={a} tenant={tenant} onOpen={() => setOpen(images.indexOf(a))} />
            ) : (
              <Chip attachment={a} tenant={tenant} />
            )}
          </li>
        ))}
      </ul>
      {open !== null && images[open] && (
        <Lightbox
          images={images}
          index={open}
          tenant={tenant}
          onIndex={setOpen}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

export interface Staged {
  /** Stable across renders so removing one does not renumber the rest. */
  key: string;
  file: File;
  state: 'waiting' | 'uploading' | 'failed';
  error?: string;
}

/**
 * Files chosen but not yet sent.
 *
 * Removable while waiting, and NOT removable once uploading — at that point
 * the bytes are going somewhere and a remove button would be a lie. A failed
 * one stays visible with its reason, because the alternative is a file that
 * silently did not arrive.
 */
export function StagedFiles({
  staged,
  onRemove,
}: {
  staged: Staged[];
  onRemove: (key: string) => void;
}) {
  if (staged.length === 0) return null;
  const failed = staged.filter((s) => s.state === 'failed');

  return (
    <>
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {staged.map((s) => (
        <li
          key={s.key}
          className={`inline-flex max-w-[18rem] items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
            s.state === 'failed'
              ? 'border-danger/40 bg-danger-tint/30 text-danger'
              : 'border-line bg-surface-sunken text-ink-secondary'
          }`}
          title={s.error ?? `${s.file.name} · ${humanSize(s.file.size)}`}
        >
          {s.state === 'uploading' ? (
            <Loader2 aria-hidden className="h-3.5 w-3.5 shrink-0 animate-spin text-brand" />
          ) : (
            iconFor(s.file.type, 'h-3.5 w-3.5 shrink-0 text-ink-tertiary')
          )}
          <span className="truncate">{s.file.name}</span>
          <span className="shrink-0 text-2xs opacity-70">{humanSize(s.file.size)}</span>
          {s.state !== 'uploading' && (
            <button
              type="button"
              aria-label={`Remove ${s.file.name}`}
              onClick={() => onRemove(s.key)}
              className="shrink-0 rounded-sm p-0.5 text-ink-tertiary hover:bg-line-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <X aria-hidden className="h-3 w-3" />
            </button>
          )}
        </li>
      ))}
    </ul>
    {/*
      WHY it failed, in the open.

      The reason was only ever a hover title, and this is the one failure
      in the composer where hovering is the wrong ask: by the time a file
      fails the message has ALREADY been sent, so the agent is not deciding
      whether to retry — they are finding out what the requester did not
      receive. "Too large" and "storage not configured" lead to completely
      different next steps, and neither is guessable from a red chip.
    */}
    {failed.length > 0 && (
      <ul className="mt-1.5 space-y-0.5">
        {failed.map((f) => (
          <li key={`${f.key}-why`} className="text-2xs leading-relaxed text-danger">
            <span className="font-medium">{f.file.name}</span> — {f.error ?? 'could not be uploaded'}
          </li>
        ))}
      </ul>
    )}
    </>
  );
}

/**
 * The attach control, and the surface that accepts a drop or a paste.
 *
 * THREE WAYS TO ADD A FILE, because a service desk gets all three:
 *
 *   - the paperclip, for somebody who has a file and knows where it is;
 *   - a DROP anywhere on the composer, which is what people try first;
 *   - a PASTE, which is how a screenshot arrives. Ctrl+V into the reply box
 *     is the fastest path from "here is what I see" to "here is what I see",
 *     and every other route makes somebody save a PNG to their desktop first.
 *
 * The drop target is the whole composer rather than a dotted rectangle: a
 * rectangle that only accepts files in one corner is a rectangle people miss.
 */
export function AttachControl({
  onFiles,
  disabled = false,
  children,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const take = useCallback(
    (list: FileList | null | undefined) => {
      const files = Array.from(list ?? []);
      if (files.length > 0) onFiles(files);
    },
    [onFiles],
  );

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (!disabled) take(e.dataTransfer?.files);
  };

  return (
    <div
      onDragOver={(e) => {
        // Without preventDefault the browser navigates to the file, which
        // loses whatever was typed in the composer.
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      onPaste={(e) => {
        if (disabled) return;
        const files = Array.from(e.clipboardData?.files ?? []);
        if (files.length > 0) {
          // Only when there ARE files: pasting text must stay a text paste.
          e.preventDefault();
          onFiles(files);
        }
      }}
      className={`relative rounded-lg transition-colors ${
        over ? 'ring-2 ring-brand ring-offset-1 ring-offset-surface' : ''
      }`}
    >
      {children}
      {over && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-brand-tint/70 text-xs font-medium text-brand">
          Drop to attach
        </div>
      )}
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          take(e.target.files);
          // Cleared so choosing the SAME file twice still fires a change.
          e.target.value = '';
        }}
      />
      <button
        type="button"
        data-attach-trigger
        disabled={disabled}
        onClick={() => input.current?.click()}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-2xs text-ink-tertiary transition-colors hover:bg-line-subtle hover:text-ink-secondary disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      >
        <Paperclip aria-hidden className="h-3.5 w-3.5" />
        Attach
      </button>
    </div>
  );
}
